<template>
  <div class="layer-panel">
    <!-- Layers List -->
    <div class="layers-list">
      <template v-for="layer in availableLayers">
        <div
          :key="layer.id"
          class="layer-item"
          :class="{ 'layer-item--active': isLayerActive(layer.id) }"
        >
          <!-- Layer Icon and Name -->
          <div class="layer-info">
            <div class="layer-icon" :style="{ backgroundColor: layer.color }">
              <ThemeIcon :icon="layer.icon" type="icons" classes="icon" />
            </div>
            <div class="layer-details">
              <span class="layer-name">{{ layer.name }}</span>
              <span v-if="isLayerActive(layer.id)" class="layer-badge layer-badge--active">Active</span>
              <span v-else-if="layer.badge" class="layer-badge">{{ layer.badge }}</span>
            </div>
          </div>

          <!-- Layer Actions -->
          <div class="layer-actions">
            <!-- Info Button -->
            <ThemedButton
              variant="ghost"
              size="icon"
              @click="showLayerInfo(layer)"
              :aria-label="`Information about ${layer.name}`"
            >
              <ThemeIcon icon="info-icon" type="icons" classes="w-5 h-5" />
            </ThemedButton>

            <!-- Loading spinner (shown while layer data is fetching) -->
            <span
              v-if="isLayerLoading(layer.id)"
              class="layer-loading-spinner"
              :aria-label="`Loading ${layer.name}`"
            ></span>

            <!-- Toggle Switch -->
            <ThemedToggle
              :value="isLayerActive(layer.id)"
              :disabled="isLayerLoading(layer.id)"
              @input="onToggleChange(layer, $event)"
              :aria-label="`Toggle ${layer.name} layer`"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- No Layers Message -->
    <div v-if="availableLayers.length === 0" class="no-layers">
      <ThemeIcon icon="inbox" type="icons" classes="w-12 h-12 mb-3" />
      <p class="no-layers-text">No layers available</p>
    </div>
  </div>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedToggle from '../ThemedToggle.vue';

export default {
  name: 'LayerPanel',

  components: {
    ThemeIcon,
    ThemedButton,
    ThemedToggle
  },
  
  props: {
    currentZoom: {
      type: Number,
      default: 4
    },
    activeLayers: {
      type: Array,
      default: () => []
    },
    loadingLayers: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      availableLayers: [
        {
          id: 'directions',
          name: 'Directions',
          icon: 'navigation',
          color: '#10b981',
          description: 'Route planning and directions',
          minZoom: 0
        },
        {
          id: 'satellite',
          name: 'Satellite',
          icon: 'satellite',
          color: '#6366f1',
          description: 'Satellite imagery view',
          minZoom: 0
        },
        {
          id: 'traffic',
          name: 'Traffic',
          icon: 'lightning',
          color: '#10b981',
          description: 'Real-time traffic conditions',
          minZoom: 0
        },
        {
          id: 'travel_time',
          name: 'Travel Time',
          icon: 'clock',
          color: '#3b82f6',
          description: 'Travel time analysis',
          minZoom: 0
        },
        {
          id: 'transit',
          name: 'Transit',
          icon: 'transit-icon',
          color: '#8b5cf6',
          description: 'Public transportation routes and stops',
          minZoom: 0
        },
        {
          id: 'wildfires',
          name: 'Wildfires',
          icon: 'fire',
          color: '#ef4444',
          description: 'Active wildfire locations',
          minZoom: 0
        },
        {
          id: 'team',
          name: 'Team',
          icon: 'users',
          color: '#a855f7',
          description: 'Team member locations',
          minZoom: 0
        },
        {
          id: 'customers',
          name: 'Customers',
          icon: 'user',
          color: '#3b82f6',
          description: 'Customer locations',
          minZoom: 0
        },
        {
          id: 'protests',
          name: 'Protests',
          icon: 'protest-new',
          color: '#f59e0b',
          description: 'Protest activity',
          minZoom: 0
        },
        {
          id: 'airports',
          name: 'Airports',
          icon: 'plane-new',
          color: '#3b82f6',
          description: 'Airport locations',
          minZoom: 0
        },
        {
          id: 'ports',
          name: 'Ports',
          icon: 'anchor-new',
          color: '#06b6d4',
          description: 'Seaport locations',
          minZoom: 0
        },
        {
          id: 'highways',
          name: 'Highways',
          icon: 'road',
          color: '#6366f1',
          description: 'Major highway routes',
          minZoom: 0
        },
        {
          id: 'waterways',
          name: 'Waterways',
          icon: 'water-icon',
          color: '#0ea5e9',
          description: 'Navigable waterways and shipping channels',
          minZoom: 0
        },
        {
          id: 'rail',
          name: 'Rail',
          icon: 'train-icon',
          color: '#b45309',
          description: 'Railroad networks and freight lines',
          minZoom: 0
        }
      ]
    };
  },
  
  methods: {
    isLayerActive(layerId) {
      return this.activeLayers.includes(layerId);
    },

    isLayerLoading(layerId) {
      return this.loadingLayers.includes(layerId);
    },
    
    onToggleChange(layer, enabled) {
      console.log('[LayerPanel] onToggleChange called for:', layer.id, 'enabled:', enabled);
      this.$emit('layer-toggled', {
        layerId: layer.id,
        enabled: enabled
      });
    },
    
    showLayerInfo(layer) {
      // Show layer information modal or tooltip
      console.log('Show info for layer:', layer.name);
      // You could emit an event to show a modal with layer details
      this.$emit('show-layer-info', layer);
    }
  }
};
</script>

<style scoped lang="less">
@import '../../../../../less/theme/v1/components/cresiteiq2/layer-panel.less';
</style>
